import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';

export default function ShareTipModal({ isOpen, onClose, projectType, projectTitle }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (content.trim().length === 0) {
      toast.error('Please share your tip!');
      return;
    }

    if (content.trim().length > 280) {
      toast.error('Tip must be 280 characters or less');
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'communityTips'), {
        projectType: projectType || 'passover-seder',
        content: content.trim(),
        familyStyle: family?.onboarding?.startingPoint || 'just-beginning',
        kidsAges: family?.onboarding?.kids?.map(k => k.age) || [],
        approved: false, // Requires moderator approval
        upvotes: 0,
        upvotedBy: [],
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        anonymous: true,
      });

      toast.success('Tip submitted! It will appear after moderator approval.');
      setContent('');
      onClose();
    } catch (error) {
      console.error('Error submitting tip:', error);
      toast.error('Failed to submit tip. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-card shadow-card max-w-lg w-full p-6 sm:p-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-sans text-xl sm:text-2xl font-semibold text-text-primary tracking-tight">
            Share Your Hack
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-text-secondary mb-4">
          Share a helpful tip from your <strong>{projectTitle || 'experience'}</strong> that other families might find useful.
        </p>

        <form onSubmit={handleSubmit}>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What made your celebration special? Share a practical tip..."
            className="w-full h-32 p-4 border border-border rounded-lg resize-none focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-base"
            maxLength={280}
          />
          <div className="flex justify-between items-center mt-2 mb-6">
            <span className="text-xs text-text-secondary">
              {content.length}/280 characters
            </span>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-white text-text-primary border border-border hover:border-primary hover:shadow-button-hover transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || content.trim().length === 0}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Share Tip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


